import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { ApproverDistro } from "@api/types/approverDistro";
import api from "@api";
import { useApproverDistroStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<ApproverDistro>;
}

type Key = keyof ApproverDistro;

export const ApproverDistroDetails: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [, setApproverDistroStore] = useApproverDistroStore();

  let approverDistro: ApproverDistro = {
    id: 0,
    name: "",
    isActive: true,
  };

  function clearAndClose() {
    approverDistro.id = 0;
    approverDistro.name = "";
    approverDistro.isActive = false;
    form.reset();
    onCancel();
  }

  useEffect(() => {
    return () => {
      setSubmitting(false);
      setApproverDistroStore({ fetchList: true });
    };
  }, []);

  const [isSubmitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitData() {
    approverDistro.id = form.values.id;
    approverDistro.name = form.values.name;
    approverDistro.isActive = form.values.isActive;
    setSubmitting(true);
    if (approverDistro.id !== 0) {
      // EDIT
      api.approverDistro
        .editApproverDistro(approverDistro.id, approverDistro)
        .then((response) => {
          alertService.success({
            msg: "Approver distro has been successfully updated.",
          });
          setApproverDistroStore({ fetchList: true });
          clearAndClose();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      // CREATE
      api.approverDistro
        .postApproverDistro(approverDistro)
        .then((response) => {
          alertService.success({
            msg: "Approver distro has been successfully saved.",
          });
          setApproverDistroStore({ fetchList: true });
          clearAndClose();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "name":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  const modalTitle = form.values.id !== 0 ? "Update" : "Add New";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  return (
    <Modal
      isOpen={open}
      title={`${modalTitle} Approver Distro`}
      closeModal={() => clearAndClose()}
    >
      <Form>
        <Form.Textbox
          label="Name"
          name="name"
          onChange={onChange}
          size={12}
          error={form.errors.name}
          value={form.values.name}
          placeholder="Enter Approver Distro Name"
          required
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          size={6}
          value={form.values.isActive}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearAndClose()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
